import { useEffect, useMemo } from 'react'
import { Info } from '@phosphor-icons/react'
import ReactTooltip from 'react-tooltip'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import TextField from '../../../../FormLegacy/FormFieldsV2/TextField'
import SelectField from '../../../../FormLegacy/FormFieldsV2/SelectField'
import ToggleField from '../../../../FormLegacy/FormFieldsV2/ToggleField'
import {
  getReservationLengthOptions,
  getTimeUnitOptions,
  onChangeNumberField,
  MAX_MIN_TIME,
  MAX_WEEKLY_LIMITER,
  MAX_BOOKING_WINDOW_LIMITER,
  MAX_RESERVATION_LENGTH,
} from '../helpers'
import CustomQuestionsSelector from '../components/CustomQuestionsSelector'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'

const ReservationLimits = ({ values, setFieldValue, handleChange, errors = {} }) => {
  const { t } = useTranslation(['reservations'])
  const { displaySnackbar } = useSnackbar()

  const minTimeOptions = useMemo(
    () => getReservationLengthOptions(values.timeInterval, values.hourlySlots, t),
    [values.timeInterval, values.hourlySlots, t],
  )

  const maxTimeOptions = useMemo(
    () => minTimeOptions.filter((option) => option.value >= values.minTime),
    [minTimeOptions, values.minTime],
  )

  useEffect(() => {
    if (values.multiDayBooking || minTimeOptions.length === 0) return

    const validMinValues = minTimeOptions.map((opt) => opt.value)

    if (values.minTime && !validMinValues.includes(values.minTime)) {
      const newMinTime = validMinValues[0]
      setFieldValue('minTime', newMinTime)
      setFieldValue('maxTime', newMinTime)
      return
    }

    const validMaxValues = validMinValues.filter((v) => v >= values.minTime)
    if (values.maxTime && !validMaxValues.includes(values.maxTime)) {
      setFieldValue('maxTime', values.minTime || validMaxValues[0])
    }
  }, [minTimeOptions, values.minTime, values.maxTime, values.multiDayBooking])

  const handleMinTimeChange = (e) => {
    const minTime = e.target.value

    if (minTime < values.timeInterval) {
      displaySnackbar(
        t('reservations:amenityForms.limits.minReservationLengthError', {
          timeInterval: values.timeInterval,
        }),
        true,
      )
      return
    }

    setFieldValue('minTime', minTime)
    setFieldValue('maxTime', minTime)
  }

  const handleMinReservationLengthChange = (e) => {
    const minReservationLength = e.target.value
    setFieldValue('minimumDays', minReservationLength)

    if (parseInt(minReservationLength) > parseInt(values.maximumDays)) {
      setFieldValue('maximumDays', minReservationLength)
    }
  }

  return (
    <div className="form-step">
      <div className="form-step__content">
        <div className="form-step__section">
          <div className="form-step__row">
            {values.multiDayBooking ? (
              <>
                <div className="form-step__column">
                  <div className="form-step__label">
                    {t('reservations:amenityForms.limits.minReservationLengthDays')}
                  </div>
                  <TextField
                    onlyIntegers={true}
                    name="minimumDays"
                    value={values.minimumDays}
                    onChange={(e) => onChangeNumberField(e, handleMinReservationLengthChange)}
                    type="number"
                    isError={!!errors.minimumDays}
                    min={0}
                    max={MAX_RESERVATION_LENGTH}
                  />
                </div>
                <div className="form-step__column">
                  <div className="form-step__label">
                    {t('reservations:amenityForms.limits.maxReservationLengthDays')}
                  </div>
                  <TextField
                    onlyIntegers={true}
                    name="maximumDays"
                    value={values.maximumDays}
                    onChange={(e) => onChangeNumberField(e, handleChange)}
                    type="number"
                    isError={!!errors.maximumDays || values?.minimumDays > values?.maximumDays}
                    min={0}
                    max={MAX_RESERVATION_LENGTH}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="form-step__column">
                  <div className="form-step__label">
                    {t('reservations:amenityForms.limits.minReservationLength')}
                  </div>
                  <SelectField
                    name="minTime"
                    value={values.minTime}
                    options={minTimeOptions}
                    onChange={handleMinTimeChange}
                    isError={!!errors.minTime}
                  />
                </div>
                <div className="form-step__column">
                  <div className="form-step__label">
                    {t('reservations:amenityForms.limits.maxReservationLength')}
                  </div>
                  <SelectField
                    name="maxTime"
                    value={values.maxTime}
                    options={maxTimeOptions}
                    onChange={handleChange}
                    isError={!!errors.maxTime}
                  />
                </div>
              </>
            )}
          </div>
          <div className="form-step__row">
            <div className="form-step__column">
              <div className="form-step__label">
                {t('reservations:amenityForms.limits.minAdvanceNotice')}
                <Info size={16} data-tip data-for="minAdvanceNotice" />
                <ReactTooltip
                  className="tooltip"
                  multiline={true}
                  id="minAdvanceNotice"
                  place="top"
                  effect="solid"
                >
                  {t('reservations:amenityForms.limits.minAdvanceNoticeTooltip')}
                </ReactTooltip>
              </div>
              <div className="form-step__row">
                <TextField
                  name="noticePeriod"
                  value={values.noticePeriod}
                  onChange={(e) => onChangeNumberField(e, handleChange)}
                  type="number"
                  onlyIntegers={true}
                  min={0}
                  max={1000}
                />
                <SelectField
                  additionalClass="field-select--small"
                  name="noticePeriodUnit"
                  value={values.noticePeriodUnit}
                  options={getTimeUnitOptions(t)}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="form-step__section">
          <div className="form-step__row form-step__row--between">
            <div>
              <div className="form-step__label">
                {t('reservations:singleReservationAmenityForm.requiresManualApproval')}
                <Info size={16} data-tip data-for="requiresManualApproval" />
                <ReactTooltip
                  className="tooltip"
                  multiline={true}
                  id="requiresManualApproval"
                  place="top"
                  effect="solid"
                >
                  {t('reservations:amenityForms.limits.requiresManualApprovalTooltip')}
                </ReactTooltip>
              </div>
            </div>
            <div>
              <ToggleField
                name="autoApproval"
                label={
                  !values.autoApproval
                    ? t('reservations:amenityForms.yes')
                    : t('reservations:amenityForms.no')
                }
                value={!values.autoApproval}
                onChange={() => setFieldValue('autoApproval', !values.autoApproval)}
                labelOnRight
              />
            </div>
          </div>
          <div className="form-step__divider" />
          {!values.multiDayBooking && (
            <>
              <div className="form-step__row form-step__row--between">
                <div>
                  <div className="form-step__label">
                    {t('reservations:amenityForms.limits.maxOccupancy')}
                    <Info size={16} data-tip data-for="maxOccupancy" />
                    <ReactTooltip
                      className="tooltip"
                      multiline={true}
                      id="maxOccupancy"
                      place="top"
                      effect="solid"
                    >
                      {t('reservations:amenityForms.limits.maxOccupancyTooltip')}
                    </ReactTooltip>
                  </div>
                </div>
              </div>
              <div className="form-step__row">
                <TextField
                  name="capacity"
                  value={values.capacity}
                  onChange={(e) => onChangeNumberField(e, handleChange)}
                  min={0}
                  max={MAX_MIN_TIME}
                  type="number"
                  onlyIntegers={true}
                  placeholder={t('reservations:amenityForms.limits.maxBookingsPerSlotPlaceholder')}
                  isError={!!errors.capacity}
                />
              </div>
              <div className="form-step__divider" />
            </>
          )}
          <div className="form-step__row form-step__row--between">
            <div>
              <div className="form-step__label">
                {t('reservations:singleReservationAmenityForm.futureReservationLimiter')}
                <Info size={16} data-tip data-for="futureReservationLimiter" />
                <ReactTooltip
                  className="tooltip"
                  multiline={true}
                  id="futureReservationLimiter"
                  place="top"
                  effect="solid"
                >
                  {t('reservations:amenityForms.limits.futureReservationLimiterTooltip')}
                </ReactTooltip>
              </div>
            </div>
            <div>
              <ToggleField
                name="showBookingWindow"
                label={
                  values.showBookingWindow
                    ? t('reservations:amenityForms.yes')
                    : t('reservations:amenityForms.no')
                }
                value={values.showBookingWindow}
                onChange={() => setFieldValue('showBookingWindow', !values.showBookingWindow)}
                labelOnRight
              />
            </div>
          </div>
          {values.showBookingWindow && (
            <div className="form-step__row">
              <TextField
                name="bookingWindow"
                value={values.bookingWindow}
                onChange={(e) => onChangeNumberField(e, handleChange)}
                type="number"
                max={MAX_BOOKING_WINDOW_LIMITER}
                min={0}
                onlyIntegers={true}
                placeholder={t('reservations:amenityForms.limits.maxDaysAheadPlaceholder')}
                isError={!!errors.bookingWindow}
              />
            </div>
          )}
          <div className="form-step__divider" />
          <div className="form-step__row form-step__row--between">
            <div>
              <div className="form-step__label">
                {t('reservations:amenityForms.limits.customBookingQuestion')}
                <Info size={16} data-tip data-for="customBookingQuestion" />
                <ReactTooltip
                  className="tooltip"
                  multiline={true}
                  id="customBookingQuestion"
                  place="top"
                  effect="solid"
                >
                  {t('reservations:amenityForms.limits.customBookingQuestionTooltip')}
                </ReactTooltip>
              </div>
            </div>
            <div>
              <ToggleField
                name="showQuestions"
                label={
                  values.showQuestions
                    ? t('reservations:amenityForms.yes')
                    : t('reservations:amenityForms.no')
                }
                value={values.showQuestions}
                onChange={handleChange}
                labelOnRight
              />
            </div>
          </div>
          {values.showQuestions && (
            <CustomQuestionsSelector
              values={values}
              setFieldValue={setFieldValue}
              errors={errors}
            />
          )}
          <div className="form-step__divider" />
          {!values.multiDayBooking && (
            <>
              <div className="form-step__row form-step__row--between">
                <div>
                  <div className="form-step__label">
                    {t('reservations:amenityForms.limits.weeklyLimiter')}
                    <Info size={16} data-tip data-for="weeklyLimiter" />
                    <ReactTooltip
                      className="tooltip"
                      multiline={true}
                      id="weeklyLimiter"
                      place="top"
                      effect="solid"
                    >
                      {t('reservations:amenityForms.limits.weeklyLimiterTooltip')}
                    </ReactTooltip>
                  </div>
                </div>
                <div>
                  <ToggleField
                    name="showWeeklyLimiter"
                    label={
                      values.showWeeklyLimiter
                        ? t('reservations:amenityForms.yes')
                        : t('reservations:amenityForms.no')
                    }
                    value={values.showWeeklyLimiter}
                    onChange={handleChange}
                    labelOnRight
                  />
                </div>
              </div>
              {values.showWeeklyLimiter && (
                <div className="form-step__row">
                  <TextField
                    name="weeklyLimiter"
                    value={values.weeklyLimiter}
                    onChange={(e) => onChangeNumberField(e, handleChange)}
                    type="number"
                    onlyIntegers={true}
                    max={MAX_WEEKLY_LIMITER}
                    min={0}
                    placeholder={t(
                      'reservations:amenityForms.limits.maxBookingsPerWeekPlaceholder',
                    )}
                    isError={!!errors.weeklyLimiter}
                  />
                </div>
              )}
              <div className="form-step__divider" />
              <div className="form-step__row form-step__row--between">
                <div>
                  <div className="form-step__label">
                    {t('reservations:singleReservationAmenityForm.limitOnePerDay')}
                    <Info size={16} data-tip data-for="limitOnePerDay" />
                    <ReactTooltip
                      className="tooltip"
                      multiline={true}
                      id="limitOnePerDay"
                      place="top"
                      effect="solid"
                    >
                      {t('reservations:amenityForms.limits.limitOnePerDayTooltip')}
                    </ReactTooltip>
                  </div>
                </div>
                <div>
                  <ToggleField
                    name="isOnePerDay"
                    label={
                      values.isOnePerDay
                        ? t('reservations:amenityForms.yes')
                        : t('reservations:amenityForms.no')
                    }
                    value={values.isOnePerDay}
                    onChange={handleChange}
                    labelOnRight
                  />
                </div>
              </div>
              <div className="form-step__divider" />
              <div className="form-step__row form-step__row--between">
                <div>
                  <div className="form-step__label">
                    {t('reservations:amenityForms.limits.limitOnePerUserPerDay')}
                    <Info size={16} data-tip data-for="limitOneReservationPerUserPerDay" />
                    <ReactTooltip
                      className="tooltip"
                      multiline={true}
                      id="limitOneReservationPerUserPerDay"
                      place="top"
                      effect="solid"
                    >
                      {t('reservations:amenityForms.limits.limitOnePerUserPerDayTooltip')}
                    </ReactTooltip>
                  </div>
                </div>
                <div>
                  <ToggleField
                    name="limitDailyReservations"
                    label={
                      values.limitDailyReservations
                        ? t('reservations:amenityForms.yes')
                        : t('reservations:amenityForms.no')
                    }
                    value={values.limitDailyReservations}
                    onChange={handleChange}
                    labelOnRight
                  />
                </div>
              </div>
              <div className="form-step__divider" />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

ReservationLimits.propTypes = {
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
}

export default ReservationLimits
